import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, AlertTriangle } from 'lucide-react';

// Warning threshold — GRN purchase rate differing from Master Catalogue's
// current inward rate by more than this fraction lights up an amber badge.
const PRICE_CHANGE_ALERT_THRESHOLD = 0.10;

function GRNItemRow({ 
  item, 
  idx, 
  products, 
  canUpdateMrp, 
  highlighted,
  onUpdateItem,
  onUpdateAlternate,
  onRemove,
  itemRef
}) {
  const totalOrdered = item.total_ordered || item.ordered_quantity;
  const prevReceived = item.previously_received || 0;
  const pendingQty = item.pending_qty || item.ordered_quantity;

  return (
    <tr 
      ref={itemRef}
      className={`hover:bg-gray-50 transition-all duration-300 ${item.is_alternate ? 'bg-blue-50' : ''} ${highlighted ? 'bg-green-100 ring-2 ring-green-500 ring-inset' : ''}`} 
      data-testid={`grn-item-${idx}`}
    >
      <td className="px-3 py-2">
        {item.is_alternate ? (
          <Select
            value={item.product_id}
            onValueChange={(val) => onUpdateAlternate(idx, 'product_id', val)}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Select product..." />
            </SelectTrigger>
            <SelectContent>
              {products.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div>
            <p className="font-medium flex items-center gap-1">
              {item.product_name}
              {(() => {
                // Flash an amber icon when the PO rate differs from the
                // Master Catalogue inward rate by >10%. Tooltip gives the
                // exact delta so procurement knows what changed.
                const poRate = Number(item.po_rate) || 0;
                const catRate = Number(item.catalogue_rate) || 0;
                if (!poRate || !catRate) return null;
                const deltaPct = Math.abs(poRate - catRate) / catRate;
                if (deltaPct <= PRICE_CHANGE_ALERT_THRESHOLD) return null;
                const direction = poRate > catRate ? '↑' : '↓';
                const signed = ((poRate - catRate) / catRate) * 100;
                return (
                  <span
                    className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5"
                    title={`PO rate ₹${poRate.toFixed(2)} vs Master ₹${catRate.toFixed(2)} — ${signed.toFixed(1)}% change. Verify with procurement before accepting stock.`}
                    data-testid={`price-alert-${idx}`}
                  >
                    <AlertTriangle size={12} />
                    {direction}{Math.abs(signed).toFixed(0)}%
                  </span>
                );
              })()}
            </p>
            {item.product_barcode && (
              <p className="text-xs text-slate-500 font-mono">{item.product_barcode}</p>
            )}
          </div>
        )}
      </td>
      
      <td className="px-2 py-2 text-center font-medium text-blue-600 bg-blue-50/50">
        {totalOrdered}
      </td>
      
      <td className="px-2 py-2 text-center font-medium text-green-600 bg-green-50/50">
        {prevReceived}
      </td>
      
      <td className="px-2 py-2 text-center font-bold text-orange-600 bg-orange-50/50">
        {pendingQty}
      </td>
      
      <td className="px-2 py-2 bg-yellow-50/50">
        <Input
          type="number"
          min="0"
          max={pendingQty}
          step="0.01"
          value={item.received_quantity}
          onChange={(e) => onUpdateItem(idx, 'received_quantity', e.target.value)}
          className="h-8 text-center w-full font-medium"
          data-field="received_quantity"
          data-testid={`received-qty-${idx}`}
        />
      </td>
      
      <td className="px-2 py-2 text-center">
        {item.is_alternate ? '-' : (
          <span className={item.shortage_quantity > 0 ? 'text-yellow-600 font-semibold' : 'text-gray-400'}>
            {item.shortage_quantity}
          </span>
        )}
      </td>
      
      <td className="px-2 py-2">
        <Input
          type="number"
          min="0"
          step="1"
          value={item.damaged_quantity}
          onChange={(e) => item.is_alternate
            ? onUpdateAlternate(idx, 'damaged_quantity', e.target.value)
            : onUpdateItem(idx, 'damaged_quantity', e.target.value)
          }
          className="h-8 text-center"
          data-testid={`grn-damaged-${idx}`}
        />
      </td>
      
      <td className="px-2 py-2 text-center bg-green-100/50">
        <span className="font-bold text-green-700">{item.accepted_quantity}</span>
      </td>
      
      <td className="px-2 py-2 text-center">
        <span className="font-medium text-gray-600">₹{item.current_mrp || 0}</span>
      </td>
      
      {canUpdateMrp && (
        <td className="px-2 py-2 bg-green-50">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={item.new_mrp || ''}
            onChange={(e) => item.is_alternate
              ? onUpdateAlternate(idx, 'new_mrp', e.target.value)
              : onUpdateItem(idx, 'new_mrp', e.target.value)
            }
            className={`h-8 text-center ${item.new_mrp !== item.current_mrp ? 'border-green-500 bg-green-100' : ''}`}
            data-testid={`grn-mrp-${idx}`}
          />
        </td>
      )}
      
      <td className="px-2 py-2">
        <Input
          value={item.batch_number}
          onChange={(e) => onUpdateItem(idx, 'batch_number', e.target.value)}
          placeholder="Batch"
          className="h-8 text-center text-xs"
        />
      </td>
      
      <td className="px-2 py-2">
        <Input
          type="date"
          value={item.expiry_date}
          onChange={(e) => onUpdateItem(idx, 'expiry_date', e.target.value)}
          className="h-8 text-xs"
        />
      </td>
      
      <td className="px-2 py-2">
        {item.is_alternate && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onRemove(idx)}
            className="h-7 w-7 p-0 text-red-500"
          >
            <X size={14} />
          </Button>
        )}
      </td>
    </tr>
  );
}

export { GRNItemRow };
